import React, { useState } from 'react';
import { WModal } from 'shared_components/components/WModal/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { sendToast } from 'shared_components/utils/Toast';

const SHOW_DEV_TOOLS = process.env.SHOW_DEV_TOOLS === 'true';

export function DevToolsFloatingButton() {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<string | null>(null);
  const [parentRecordingIdInput, setParentRecordingIdInput] = useState('');
  const [lookupSourceId, setLookupSourceId] = useState('');
  const [lookupSource, setLookupSource] = useState<
    'discogs' | 'MusicBrainz' | 'spotify' | 'deezer' | 'ppl'
  >('discogs');
  const { adminApi: api } = useApi();
  const { clientId } = useClientId();

  const userId = clientId != null ? String(clientId) : '';
  const parentRecordingId = parentRecordingIdInput.trim();
  const sourceId = lookupSourceId.trim();
  const source = lookupSource;

  const showResult = (result: unknown, error = false) => {
    const str =
      typeof result === 'string' ? result : JSON.stringify(result, null, 2);
    setLastResponse(str);
    if (error) {
      sendToast({
        text: 'Request failed – see result below',
        variant: 'error',
      });
    } else {
      sendToast({
        text: 'Request completed – see result below',
        variant: 'success',
      });
    }
  };

  const runReleaseLookup = async () => {
    if (!userId.trim()) {
      sendToast({ text: 'Select a client first (clientId)', variant: 'error' });
      setLastResponse('Missing userId (clientId).');
      return;
    }
    if (!parentRecordingId.trim()) {
      setLastResponse(
        'Missing parentRecordingId. Use a real recording ID from discography.'
      );
      sendToast({
        text: 'Set parentRecordingId for full test',
        variant: 'error',
      });
      return;
    }
    if (!sourceId) {
      setLastResponse(
        'Missing source ID for lookup (e.g. Discogs release ID).'
      );
      sendToast({ text: 'Set source ID for lookup', variant: 'error' });
      return;
    }
    setLoading('lookup');
    try {
      const data = await api.lookupLookupReleaseResponse({
        userId,
        parentRecordingId,
        sourceId,
        source,
      });
      showResult(data);
    } catch (e: unknown) {
      const err = e as { response?: Response; status?: number };
      const status = err.response?.status ?? err.status;
      let body: unknown = (e as { body?: unknown })?.body;
      try {
        if (err.response) body = await (err.response as Response).json?.();
      } catch {
        // ignore
      }
      showResult({ status, error: String(e), body }, true);
    } finally {
      setLoading(null);
    }
  };

  const runCreateAndLink = async () => {
    if (!userId.trim() || !parentRecordingId.trim()) {
      sendToast({
        text: 'Need client and parentRecordingId',
        variant: 'error',
      });
      setLastResponse(
        'Set clientId and parentRecordingId (e.g. from discography).'
      );
      return;
    }
    setLoading('create');
    try {
      const data = await api.linkReleaseServiceResponse({
        userId,
        parentRecordingId,
        releaseRequest: {
          releaseTitle: 'Dev tools test release',
          sourceId: lookupSourceId,
          source: lookupSource,
        },
      });
      showResult(data);
    } catch (e: unknown) {
      const err = e as { response?: Response };
      let body: unknown = (e as { body?: unknown })?.body;
      try {
        if (err.response) body = await (err.response as Response).json?.();
      } catch {
        // ignore
      }
      showResult({ error: String(e), body }, true);
    } finally {
      setLoading(null);
    }
  };

  const runLinkRelease = async () => {
    if (!userId.trim() || !parentRecordingId.trim()) {
      sendToast({
        text: 'Need client and parentRecordingId',
        variant: 'error',
      });
      setLastResponse('Set clientId and parentRecordingId.');
      return;
    }
    setLoading('link');
    try {
      const data = await api.linkReleaseServiceResponse({
        userId,
        parentRecordingId,
        releaseRequest: {
          releaseId: 'placeholder-release-id',
          releaseTitle: 'Dev tools test release',
        },
      });
      showResult(data);
    } catch (e: unknown) {
      const err = e as { response?: Response };
      let body: unknown = (e as { body?: unknown })?.body;
      try {
        if (err.response) body = await (err.response as Response).json?.();
      } catch {
        // ignore
      }
      showResult({ error: String(e), body }, true);
    } finally {
      setLoading(null);
    }
  };

  const runCorrectRelease = async () => {
    if (!userId.trim() || !parentRecordingId.trim()) {
      sendToast({
        text: 'Need client and parentRecordingId',
        variant: 'error',
      });
      setLastResponse('Set clientId and parentRecordingId.');
      return;
    }
    setLoading('correct');
    try {
      const data = await api.correctServiceResponse({
        userId,
        parentRecordingId,
        correctReleaseRequest: {
          releaseId: 'placeholder-release-id',
          edits: [],
        },
      });
      showResult(data);
    } catch (e: unknown) {
      const err = e as { response?: Response };
      let body: unknown = (e as { body?: unknown })?.body;
      try {
        if (err.response) body = await (err.response as Response).json?.();
      } catch {
        // ignore
      }
      showResult({ error: String(e), body }, true);
    } finally {
      setLoading(null);
    }
  };

  if (!SHOW_DEV_TOOLS) return null;

  const busy = loading !== null;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="tw-fixed tw-bottom-6 tw-right-6 tw-z-40 tw-w-12 tw-h-12 tw-rounded-full tw-bg-surfaceLime tw-text-surfaceBlack90 tw-shadow-lg tw-flex tw-items-center tw-justify-center hover:tw-opacity-90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-surfaceLime focus:tw-ring-offset-2 focus:tw-ring-offset-surfaceBlack80"
        title="Dev tools"
        aria-label="Open dev tools"
      >
        <i className="wi wi-sm wi-settings" />
      </button>

      <WModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Backend integration tests"
        modalClasses="tw-min-w-[320px] tw-max-w-lg"
      >
        <div className="tw-space-y-3">
          <p className="tw-text-sm tw-text-textWhite/80 tw-mb-2">
            Run release management endpoints. Uses current client as userId when
            set.
          </p>
          {userId ? (
            <p className="tw-text-xs tw-text-textWhite/60">
              userId:{' '}
              <code className="tw-bg-surfaceBlack80 tw-px-1 tw-rounded">
                {userId}
              </code>
            </p>
          ) : (
            <p className="tw-text-xs tw-text-westburyRed">
              No client selected – set clientId for release tests.
            </p>
          )}
          <label className="tw-block tw-text-xs tw-text-textWhite/70 tw-mt-1">
            parentRecordingId (from discography)
            <input
              type="text"
              value={parentRecordingIdInput}
              onChange={(e) => setParentRecordingIdInput(e.target.value)}
              placeholder="e.g. UUID"
              className="tw-w-full tw-mt-1 tw-px-2 tw-py-1.5 tw-rounded tw-bg-surfaceBlack80 tw-text-textWhite tw-text-sm tw-border tw-border-textWhite/20"
            />
          </label>
          <div className="tw-flex tw-gap-2 tw-items-end">
            <label className="tw-flex-1 tw-text-xs tw-text-textWhite/70">
              Lookup source ID
              <input
                type="text"
                value={lookupSourceId}
                onChange={(e) => setLookupSourceId(e.target.value)}
                placeholder="e.g. 12345 (Discogs)"
                className="tw-w-full tw-mt-1 tw-px-2 tw-py-1.5 tw-rounded tw-bg-surfaceBlack80 tw-text-textWhite tw-text-sm tw-border tw-border-textWhite/20"
              />
            </label>
            <label className="tw-flex-1 tw-text-xs tw-text-textWhite/70">
              Lookup source
              <select
                value={lookupSource}
                onChange={(e) =>
                  setLookupSource(e.target.value as typeof lookupSource)
                }
                className="tw-w-full tw-mt-1 tw-px-2 tw-py-1.5 tw-rounded tw-bg-surfaceBlack80 tw-text-textWhite tw-text-sm tw-border tw-border-textWhite/20"
              >
                <option value="discogs">discogs</option>
                <option value="MusicBrainz">MusicBrainz</option>
                <option value="spotify">spotify</option>
                <option value="deezer">deezer</option>
                <option value="ppl">ppl</option>
              </select>
            </label>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-xs tw-text-textWhite/70 tw-font-medium tw-mt-2">
              Release management
            </span>
            <WButton
              variant="secondary"
              label={
                loading === 'lookup' ? 'Lookup release…' : 'Release lookup'
              }
              onClick={runReleaseLookup}
              disabled={busy}
            />
            <WButton
              variant="secondary"
              label={
                loading === 'create'
                  ? 'Create & link…'
                  : 'Create release and link (POST)'
              }
              onClick={runCreateAndLink}
              disabled={busy}
            />
            <WButton
              variant="secondary"
              label={loading === 'link' ? 'Link…' : 'Link release (POST)'}
              onClick={runLinkRelease}
              disabled={busy}
            />
            <WButton
              variant="secondary"
              label={
                loading === 'correct' ? 'Correct…' : 'Correct release (POST)'
              }
              onClick={runCorrectRelease}
              disabled={busy}
            />
          </div>

          {lastResponse !== null && (
            <div className="tw-mt-4 tw-pt-3 tw-border-t tw-border-textWhite/20">
              <p className="tw-text-xs tw-text-textWhite/70 tw-mb-1">
                Last response:
              </p>
              <pre className="tw-text-xs tw-bg-surfaceBlack80 tw-p-3 tw-rounded tw-overflow-auto tw-max-h-48 tw-text-textWhite">
                {lastResponse}
              </pre>
            </div>
          )}
        </div>
      </WModal>
    </>
  );
}
